import type { CSSProperties } from 'react'
import type { RoomAvailability } from '../api/bookings'

/**
 * The studio's rooms as chips: the free ones pickable, the busy ones struck
 * through and naming what is in them (D-388, D-897).
 *
 * **Lifted out of `BookingForm` so the move panel can look the same.** `asked`,
 * twice — *"when moving a class to a different time can we have it look
 * consistent with how scheduling a private looks"*, and then, after a first
 * attempt matched the wrong screen, *"the move this night still doesn't seem to
 * look the same as private booking"*. Scheduling a private is this: a wrapped
 * row of coloured chips, not the dropdown the ask row uses.
 *
 * **Presentational on purpose.** It is handed an answer rather than working one
 * out, because *is this room free* already has exactly one implementation
 * (`roomAvailability`, D-396) and the thing that was duplicated here was the
 * markup. Two callers compute availability differently — the booking form from
 * a slot it is also drawing a grid for, the picker from three of its own
 * queries — and that difference is real.
 */
export default function RoomChoice({
  options,
  roomId,
  onPick,
  currentRoomId,
}: {
  options: RoomAvailability[]
  roomId: string
  onPick: (id: string) => void
  /** Where it already meets, when something is being moved rather than booked (D-896). */
  currentRoomId?: string
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((room) => {
        const selected = room.roomId === roomId

        return (
          <button
            key={room.roomId}
            type="button"
            disabled={!room.isFree}
            onClick={() => onPick(room.roomId)}
            title={room.conflictLabel ?? `${room.name} is free`}
            className={`room-tint flex min-h-[36px] items-center gap-1.5 rounded-full border px-3 text-[12.5px] font-semibold transition-colors disabled:cursor-not-allowed ${
              selected
                ? 'border-accent-2 bg-accent-2 text-on-accent'
                : room.isFree
                  ? 'border-line-2 bg-surface hover:border-ink-3'
                  : 'border-line bg-surface-2 text-ink-3 line-through'
            }`}
            style={{ '--room-color': room.color ?? 'var(--color-ink-3)' } as CSSProperties}
          >
            <span
              className="size-2 shrink-0 rounded-full"
              style={{ background: selected ? 'currentColor' : 'var(--room-display)' }}
              aria-hidden="true"
            />

            <span className="min-w-0 truncate">{room.name}</span>

            {/*
              <b>Where it already meets, marked in both states</b> (D-896). With
              her own room busy nothing is selected and the server's refusal
              names no room, so an unmarked row leaves her reading six names
              without knowing which one is the problem.
            */}
            {room.roomId === currentRoomId && (
              <span className="shrink-0 text-[11px] font-normal opacity-80">now</span>
            )}

            {/*
              **A busy room says what is in it** (D-388). The chips put this in
              a `title` when they replaced the cards, which is no use on a phone
              and unreachable by keyboard — the chip is `disabled`, so it cannot
              even be focused. This project has shipped a `title` standing in
              for the thing it named once before.
            */}
            {!room.isFree && room.conflictLabel && (
              <span className="truncate text-[11px] font-normal opacity-80">
                {room.conflictLabel}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
